import {
  adminApiClient,
  extractDownloadFilename,
  resolveBlobApiErrorMessage,
} from '@/services/admin/baseService'
import type { NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { buildModeAwareConfig } from '@/services/admin/modeAware'
import type {
  ApiResponse,
  ExtendSweepUnlockPayload,
  ExtendSweepUnlockResponse,
  SettlementBatchResponse,
  SettlementCandidate,
  SettlementGuardInfo,
  SettlementRecordsResponse,
  SettlementSingleResponse,
  SettlementVerificationEnqueueResponse,
} from '@/types'

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function requireBoolean(value: unknown, field: string): boolean {
  if (typeof value !== 'boolean') {
    throw new Error(`Invalid settlement API response: ${field} must be a boolean`)
  }
  return value
}

function requireNonNegativeInteger(value: unknown, field: string): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) {
    throw new Error(`Invalid settlement API response: ${field} must be a non-negative integer`)
  }
  return value
}

function requireSettlementBatchResponse(value: unknown): SettlementBatchResponse {
  if (!isRecord(value)) {
    throw new Error('Invalid settlement API response: batch payload must be an object')
  }
  requireBoolean(value.success, 'success')
  if (!isRecord(value.guard)) {
    throw new Error('Invalid settlement API response: guard must be an object')
  }
  if (!Array.isArray(value.results)) {
    throw new Error('Invalid settlement API response: results must be an array')
  }
  for (const [index, result] of value.results.entries()) {
    if (
      !isRecord(result)
      || typeof result.orderId !== 'string'
      || typeof result.eligible !== 'boolean'
      || typeof result.queued !== 'boolean'
    ) {
      throw new Error(`Invalid settlement API response: results[${index}] is malformed`)
    }
  }
  return value as unknown as SettlementBatchResponse
}

export const settlementService = {
  async exportCsv(params?: {
    status?: string
    order_id?: string
  }, mode?: NetworkEnvironment): Promise<{ blob: Blob; filename: string }> {
    try {
      const response = await adminApiClient.get<Blob>('/admin/settlements/export', {
        params,
        responseType: 'blob',
        ...buildModeAwareConfig(mode),
      })

      return {
        blob: response.data,
        filename: extractDownloadFilename(
          response.headers['content-disposition'],
          `settlement-records-${new Date().toISOString().slice(0, 10)}.csv`
        ),
      }
    } catch (error) {
      throw new Error(await resolveBlobApiErrorMessage(error, 'Failed to export settlement records'))
    }
  },

  async previewSettlements(payload: {
    orderIds?: string[]
    chainId?: string
    limit?: number
  }, mode?: NetworkEnvironment): Promise<{ success: boolean; queuedCount: number; totalRequested: number; error?: string }> {
    const response = await adminApiClient.post<unknown>(
      '/admin/settlements/preview',
      payload,
      buildModeAwareConfig(mode)
    )

    if (!isRecord(response.data)) {
      throw new Error('Invalid settlement API response: preview payload must be an object')
    }

    return {
      success: requireBoolean(response.data.success, 'success'),
      queuedCount: requireNonNegativeInteger(response.data.queuedCount, 'queuedCount'),
      totalRequested: requireNonNegativeInteger(
        response.data.totalRequested,
        'totalRequested'
      ),
      error: typeof response.data.error === 'string' ? response.data.error : undefined,
    }
  },
  async enqueueSettlements(
    payload: { orderIds?: string[]; limit?: number; force?: boolean; forceReason?: string },
    mode?: NetworkEnvironment
  ): Promise<SettlementBatchResponse> {
    const response = await adminApiClient.post<SettlementBatchResponse>(
      '/admin/settlements/trigger',
      payload,
      buildModeAwareConfig(mode)
    )

    return requireSettlementBatchResponse(response.data)
  },

  async getSettlementGuard(
    mode?: NetworkEnvironment
  ): Promise<{ success: boolean; guard: SettlementGuardInfo }> {
    const response = await adminApiClient.get<{ success: boolean; guard: SettlementGuardInfo }>(
      '/admin/settlements/guard',
      buildModeAwareConfig(mode)
    )

    if (!response.data) {
      throw new Error('Failed to fetch settlement guard configuration')
    }

    return response.data
  },

  async extendSweepUnlock(
    orderId: string,
    payload: ExtendSweepUnlockPayload,
    mode?: NetworkEnvironment
  ): Promise<ExtendSweepUnlockResponse> {
    const response = await adminApiClient.post<ApiResponse<ExtendSweepUnlockResponse>>(
      `/admin/orders/${orderId}/extend-unlock`,
      payload,
      buildModeAwareConfig(mode)
    )

    if (!response.data) {
      throw new Error('Failed to queue sweep unlock extension')
    }

    if (!isRecord(response.data.data)) {
      throw new Error('Invalid settlement API response: extend unlock data is missing')
    }
    const data = response.data.data
    return {
      ...data,
      success: response.data.success,
    }
  },

  async enqueueSettlementVerification(payload: {
    orderIds?: string[]
    chainId?: string
    limit?: number
    force?: boolean
  }, mode?: NetworkEnvironment): Promise<SettlementVerificationEnqueueResponse> {
    const response = await adminApiClient.post<SettlementVerificationEnqueueResponse>(
      '/admin/settlements/verification',
      payload,
      buildModeAwareConfig(mode)
    )

    if (!response.data) {
      throw new Error('Failed to enqueue settlement verification')
    }

    return response.data
  },

  async triggerSettlement(
    orderId: string,
    data?: { force?: boolean; forceReason?: string },
    mode?: NetworkEnvironment
  ): Promise<SettlementSingleResponse> {
    const response = await adminApiClient.post<
      ApiResponse<{
        guard: SettlementGuardInfo;
        settlement: SettlementCandidate;
      }>
    >(
      `/admin/orders/${orderId}/settlement`,
      data ?? {},
      buildModeAwareConfig(mode)
    )

    const payload: unknown = response.data
    if (!isRecord(payload) || !isRecord(payload.data)) {
      throw new Error('Invalid settlement API response: single settlement data is missing')
    }
    const success = requireBoolean(payload.success, 'success')
    if (!isRecord(payload.data.guard) || !isRecord(payload.data.settlement)) {
      throw new Error('Invalid settlement API response: single settlement payload is malformed')
    }
    const settlement = payload.data.settlement
    if (
      typeof settlement.orderId !== 'string'
      || typeof settlement.eligible !== 'boolean'
      || typeof settlement.queued !== 'boolean'
    ) {
      throw new Error('Invalid settlement API response: settlement result is malformed')
    }

    return {
      success,
      guard: payload.data.guard as unknown as SettlementGuardInfo,
      result: settlement as unknown as SettlementCandidate,
      error: typeof payload.error === 'string' ? payload.error : undefined,
    }
  },

  async getSettlementRecords(params?: {
    page?: number
    limit?: number
    status?: string
    order_id?: string
  }, mode?: NetworkEnvironment): Promise<SettlementRecordsResponse> {
    const searchParams = new URLSearchParams()

    if (params?.page) searchParams.append('page', params.page.toString())
    if (params?.limit) searchParams.append('limit', params.limit.toString())
    if (params?.status) searchParams.append('status', params.status)
    if (params?.order_id) searchParams.append('order_id', params.order_id)

    const response = await adminApiClient.get<SettlementRecordsResponse>(
      `/admin/settlements?${searchParams.toString()}`,
      buildModeAwareConfig(mode)
    )

    if (!response.data) {
      throw new Error('Failed to fetch settlement records')
    }

    return response.data
  },
}
